import DashboardLayout from '../components/DashboardLayout';
import { Typography, Box, CircularProgress, IconButton, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, Button, Grid, TextField, InputAdornment, MenuItem } from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { api, endpoints } from '../api/config';
import { useAuth } from '../context/AuthContext';
import { useCachedData } from '../hooks/useCachedData';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getDateInputProps } from '../utils/dateConstraints';
import {
  BUTTON_STYLE,
  DIALOG_CLOSE_BUTTON_STYLE,
  BUTTON_VARIANT,
  BUTTON_SIZE,
  NEAR_FULLSCREEN_DIALOG_PAPER_SX,
} from '../constants/styles';

const BinTransfer = () => {
  const { token } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Dialog states
  const [binDataOpen, setBinDataOpen] = useState(false);
  const [binDataLoading, setBinDataLoading] = useState(false);
  const [binDataDetails, setBinDataDetails] = useState(null);
  const [binDataNetsuiteObj, setBinDataNetsuiteObj] = useState(null);
  // NetSuite details shown inside Bin Data dialog (derived per-row)

  // Nested dialog states
  const [nestedDialogOpen, setNestedDialogOpen] = useState(false);
  const [nestedDialogLoading, setNestedDialogLoading] = useState(false);
  const [nestedDialogTitle, setNestedDialogTitle] = useState('');
  const [nestedDialogData, setNestedDialogData] = useState(null);
  const [nestedColumnVisibility, setNestedColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-nested-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Acknowledgment item details dialog states
  const [ackItemDetailsOpen, setAckItemDetailsOpen] = useState(false);
  const [ackItemDetails, setAckItemDetails] = useState([]);
  const [ackItemDetailsColumnVisibility, setAckItemDetailsColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-ack-item-details-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Default date and location for bin transfer records
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [selectedLocation, setSelectedLocation] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-location');
    return saved || 'Flemington L41';
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-date');
    return saved || '2026-06-10';
  });
  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-start-date');
    return saved || todayStr;
  });
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-end-date');
    return saved || todayStr;
  });
  const [successFilter, setSuccessFilter] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-success-filter');
    return saved || 'all'; // all | success | errors
  });

  // Fetch available locations for location filter dropdown
  const { data: locationsData } = useCachedData(endpoints.locations);
  const locationsList = useMemo(() => {
    const defaultList = ['Flemington L41', 'Flemington'];
    if (!locationsData) return defaultList;
    const raw = Array.isArray(locationsData) ? locationsData : Object.values(locationsData);
    const list = raw.map((loc) => loc.location || loc.name || loc.id).filter(Boolean);
    return Array.from(new Set([...defaultList, ...list]));
  }, [locationsData]);

  // Column visibility states
  const [binDataColumnVisibility, setBinDataColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-bin-data-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  const [netsuiteColumnVisibility, setNetsuiteColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-netsuite-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Build API endpoint with required location and date parameters
  const binTransferEndpoint = useMemo(() => {
    const base = endpoints.binTransfer;
    const params = new URLSearchParams();
    if (selectedLocation) {
      params.append('location', selectedLocation);
    }
    if (selectedDate) {
      params.append('date', selectedDate);
    }
    return `${base}?${params.toString()}`;
  }, [selectedLocation, selectedDate]);

  const { data, isLoading, error, refetch } = useCachedData(binTransferEndpoint);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-transfer-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Save location and date to localStorage
  useEffect(() => {
    localStorage.setItem('bin-transfer-location', selectedLocation);
  }, [selectedLocation]);

  useEffect(() => {
    localStorage.setItem('bin-transfer-date', selectedDate);
  }, [selectedDate]);

  // Save column visibility states to localStorage
  useEffect(() => {
    localStorage.setItem('bin-transfer-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-transfer-bin-data-column-visibility', JSON.stringify(binDataColumnVisibility));
  }, [binDataColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-transfer-netsuite-column-visibility', JSON.stringify(netsuiteColumnVisibility));
  }, [netsuiteColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-transfer-nested-column-visibility', JSON.stringify(nestedColumnVisibility));
  }, [nestedColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-transfer-ack-item-details-column-visibility', JSON.stringify(ackItemDetailsColumnVisibility));
  }, [ackItemDetailsColumnVisibility]);

  // Save date range and success filter to localStorage
  useEffect(() => {
    localStorage.setItem('bin-transfer-start-date', startDate);
  }, [startDate]);

  useEffect(() => {
    localStorage.setItem('bin-transfer-end-date', endDate);
  }, [endDate]);

  useEffect(() => {
    localStorage.setItem('bin-transfer-success-filter', successFilter);
  }, [successFilter]);

  // Helper function to convert complex objects to table format (similar to BinCountRecords)
  const convertToTableFormat = (data) => {
    if (!data || typeof data !== 'object') return [];
    
    // If data is an array, process each item
    if (Array.isArray(data)) {
      return data.map(item => convertToTableFormat(item)[0]);
    }
    
    // For single object, create a row with all properties
    const row = {};
    
    function processObject(obj, prefix = '') {
      Object.entries(obj).forEach(([key, value]) => {
        const newKey = prefix ? `${prefix}_${key}` : key;
        
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Store the object as is for netsuite_response
          if (key === 'netsuite_response' || newKey.endsWith('_netsuite_response')) {
            row[newKey] = value;
          } else {
            // Recursively flatten other nested objects
            processObject(value, newKey);
          }
        } else if (Array.isArray(value)) {
          // Store the array as is for itemData
          if (key === 'itemData' || newKey.endsWith('_itemData')) {
            row[newKey] = value;
          } else {
            // Handle other arrays by joining their values
            row[newKey] = value.join(', ');
          }
        } else {
          // Handle primitive values
          row[newKey] = value;
        }
      });
    }
    
    processObject(data);
    return [row];
  };

  // Process the data to handle nested bin_data and extract username
  const processedData = useMemo(() => {
    if (!data) return [];
    

    
    return data
      .map((record, index) => {
        let username = '';
        let binData = record.bin_data ? convertToTableFormat(record.bin_data) : [];
        
        // First, try to extract username from main record fields
        const usernameFields = [
          'username', 'UserName', 'user', 'User', 'user_id', 'userid',
          'created_by', 'modified_by', 'operator', 'operator_id',
          'createdBy', 'modifiedBy', 'createdByUser', 'modifiedByUser'
        ];
        
        for (const field of usernameFields) {
          if (record[field] && typeof record[field] === 'string' && record[field].trim()) {
            username = record[field].trim();
            break;
          }
        }
        
        // If not found in main record, check bin_data (now converted to array format)
        if (!username && binData && binData.length > 0) {
          const binDataRow = binData[0];
          
          for (const field of usernameFields) {
            if (binDataRow[field] && typeof binDataRow[field] === 'string' && binDataRow[field].trim()) {
              username = binDataRow[field].trim();
              // Remove username from binData to avoid duplication
              delete binDataRow[field];
              break;
            }
          }
        }
        
        // Deep search for username in nested objects if still not found
        if (!username) {
          const findUsernameInObject = (obj, path = '') => {
            if (!obj || typeof obj !== 'object') return null;
            
            for (const [key, value] of Object.entries(obj)) {
              const currentPath = path ? `${path}.${key}` : key;
              
              // Check if this key contains username-related terms
              if (key.toLowerCase().includes('user') || key.toLowerCase().includes('operator') || key.toLowerCase().includes('created_by') || key.toLowerCase().includes('modified_by')) {
                if (typeof value === 'string' && value.trim()) {
                  return value.trim();
                } else if (value && typeof value === 'object' && value.username) {
                  return value.username;
                }
              }
              
              // Recursively search nested objects (but avoid infinite recursion)
              if (value && typeof value === 'object' && !Array.isArray(value) && currentPath.split('.').length < 3) {
                const found = findUsernameInObject(value, currentPath);
                if (found) return found;
              }
            }
            return null;
          };
          
          const foundUsername = findUsernameInObject(record);
          if (foundUsername) {
            username = foundUsername;
          }
        }
        
        const processedRecord = {
          ...record,
          username: username || '-', // Ensure username is never empty
          bin_data: binData
        };
        
        return processedRecord;
      })
      .sort((a, b) => {
        // Sort by created_at timestamp in descending order (newest first)
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        return dateB - dateA;
      });
  }, [data]);

  // Apply success filter (All / Success / Errors)
  const filteredData = useMemo(() => {
    if (!processedData.length) return [];
    if (successFilter === 'all') return processedData;

    return processedData.filter((record) => {
      const success = record?.netsuite_response?.netsuite_acknowledgment?.success;
      const isSuccess =
        success === true || success === 'Yes' || success === 'yes' || success === 1 || success === '1';
      const isFailure =
        success === false || success === 'No' || success === 'no' || success === 0 || success === '0';

      if (successFilter === 'success') {
        return isSuccess;
      }
      if (successFilter === 'errors') {
        return isFailure;
      }
      return true;
    });
  }, [processedData, successFilter]);

  // Handle refresh click
  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to refresh data',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  // Helper function to format column header
  const formatColumnHeader = (key) => {
    if (!key || typeof key !== 'string') {
      return 'Unknown';
    }
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to create base column configuration
  const createBaseColumn = (key) => ({
    id: key || 'unknown', // Add unique id for React key tracking
    accessorKey: key || 'unknown',
    header: formatColumnHeader(key),
    minSize: 200,
    size: 250,
    enableSorting: true,
  });

  // Generate bin data detail columns dynamically
  const binDataDetailColumns = useMemo(() => {
    if (!binDataDetails) return [];
    
    // Convert single object to array for consistency
    const dataArray = Array.isArray(binDataDetails) ? binDataDetails : [binDataDetails];
    if (!dataArray.length) return [];
    
    // Get all unique keys from the first detail record, excluding username
    const keys = Object.keys(dataArray[0]).filter(key => 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'user'
    );
    
    return keys.map(key => {
      // For the location column, allow wrapping
      if (key.toLowerCase().includes('location')) {
        return {
          ...createBaseColumn(key),
          cell: info => info.getValue(),
          minSize: 250,
          size: 300,
          maxSize: 600,
          meta: { wrap: true }
        };
      }
      
      return {
        ...createBaseColumn(key),
        cell: info => {
          const value = info.getValue();
          if ((key === 'itemData' || key.endsWith('_itemData')) && Array.isArray(value)) {
            return `${value.length} items`;
          }
          if ((key === 'netsuite_response' || key.endsWith('_netsuite_response')) && value && typeof value === 'object') {
            const netsuiteResponse = value.netsuite_response || value;
            const acknowledgment = netsuiteResponse.netsuite_acknowledgment || {};
            const success = acknowledgment.success;
            return (
              <Button
                variant={BUTTON_VARIANT}
                size={BUTTON_SIZE}
                sx={{
                  ...BUTTON_STYLE,
                  backgroundColor: success ? '#81c784' : '#ef9a9a',
                  '&:hover': {
                    backgroundColor: success ? '#66bb6a' : '#e57373',
                  }
                }}
                onClick={() => {
                  setNetsuiteDetails(value);
                  setNetsuiteOpen(true);
                }}
              >
                View Response
              </Button>
            );
          }
          // Handle objects and arrays
          if (value === null || value === undefined) {
            return '-';
          }
          
          if (typeof value === 'object') {
            if (Array.isArray(value)) {
              return value.length > 0 ? `${value.length} items` : '0 items';
            }
            return JSON.stringify(value);
          }
          
          return String(value);
        },
      };
    });
  }, [binDataDetails]);

  // Build netsuite response columns for a given details object
  const buildNetsuiteColumns = (details) => {
    if (!details) return [];
    const netsuiteResponse = details.netsuite_response || details;
    const safeString = (value) => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };
    return [
      {
        id: 'message',
        accessorKey: 'message',
        header: 'Message',
        cell: () => safeString(netsuiteResponse.message),
        minSize: 150,
        size: 200,
        enableSorting: true,
      },
      {
        id: 'operation_time',
        accessorKey: 'operation_time',
        header: 'Operation Time',
        cell: () => safeString(netsuiteResponse.operation_time),
        minSize: 150,
        size: 200,
        enableSorting: true,
      },
      {
        id: 'netsuite_acknowledgment',
        accessorKey: 'netsuite_acknowledgment',
        header: 'NetSuite Acknowledgment',
        minSize: 500,
        size: 600,
        enableSorting: true,
        cell: () => {
          const ack = netsuiteResponse.netsuite_acknowledgment || {};
          const itemDetails = ack.itemDetails;
          const safeString = (value) => {
            if (value === null || value === undefined) return '-';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
          };
          return (
            <Box sx={{ minWidth: '500px', p: 1 }}>
              <Typography variant="body2" gutterBottom>
                <b>Message:</b> {safeString(ack.message)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <b>Success:</b> <span style={{ fontSize: '1.2em' }}>{ack.success ? '✅' : '❌'}</span>
              </Typography>
              {ack.inventoryAdjustmentId && (
                <Typography variant="body2" gutterBottom>
                  <b>Inventory Adjustment ID:</b> {safeString(ack.inventoryAdjustmentId)}
                </Typography>
              )}
              {ack.binTransferId && (
                <Typography variant="body2" gutterBottom>
                  <b>Bin Transfer ID:</b> {safeString(ack.binTransferId)}
                </Typography>
              )}
              {itemDetails && Array.isArray(itemDetails) && itemDetails.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant={BUTTON_VARIANT}
                    size={BUTTON_SIZE}
                    sx={BUTTON_STYLE}
                    onClick={() => {
                      setAckItemDetails(itemDetails);
                      setAckItemDetailsOpen(true);
                    }}
                  >
                    View Items ({itemDetails.length})
                  </Button>
                </Box>
              )}
            </Box>
          );
        }
      }
    ];
  };

  // Generate nested dialog columns dynamically
  const nestedDialogColumns = useMemo(() => {
    if (!nestedDialogData || !nestedDialogData.length) return [];
    
    // Get all unique keys from the first detail record, excluding username
    const keys = Object.keys(nestedDialogData[0]).filter(key => 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'user'
    );
    
    return keys.map(key => {
      const baseColumn = createBaseColumn(key);
      
      // Special handling for item data fields
      if (key === 'itemId' || key === 'item_id') {
        return {
          ...baseColumn,
          header: 'Item ID',
          size: 120,
        };
      }
      
      if (key === 'itemName' || key === 'item_name') {
        return {
          ...baseColumn,
          header: 'Item Name',
          size: 200,
        };
      }
      
      if (key === 'quantity') {
        return {
          ...baseColumn,
          header: 'Quantity',
          size: 100,
        };
      }
      
      return {
        ...baseColumn,
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined) {
            return '-';
          }
          if (typeof value === 'object') {
            if (Array.isArray(value)) {
              return value.length > 0 ? `Array (${value.length} items)` : 'Empty Array';
            }
            return JSON.stringify(value);
          }
          return String(value);
        }
      };
    });
  }, [nestedDialogData]);

  // Generate acknowledgment item details columns dynamically
  const ackItemDetailsColumns = useMemo(() => {
    if (!ackItemDetails || !ackItemDetails.length) return [];
    
    // Get all unique keys from the first item
    const keys = Object.keys(ackItemDetails[0]);
    
    return keys.map(key => {
      const baseColumn = createBaseColumn(key);
      
      // Special handling for item data fields
      if (key === 'itemId' || key === 'item_id') {
        return {
          ...baseColumn,
          header: 'Item ID',
          size: 120,
        };
      }
      
      if (key === 'itemName' || key === 'item_name') {
        return {
          ...baseColumn,
          header: 'Item Name',
          size: 200,
        };
      }
      
      if (key === 'quantity') {
        return {
          ...baseColumn,
          header: 'Quantity',
          size: 100,
        };
      }
      
      if (key === 'binNumber' || key === 'bin_number') {
        return {
          ...baseColumn,
          header: 'Bin Number',
          size: 120,
        };
      }
      
      if (key === 'location') {
        return {
          ...baseColumn,
          header: 'Location',
          size: 150,
        };
      }
      
      return {
        ...baseColumn,
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined) {
            return '-';
          }
          if (typeof value === 'object') {
            if (Array.isArray(value)) {
              return value.length > 0 ? `Array (${value.length} items)` : 'Empty Array';
            }
            return JSON.stringify(value);
          }
          return String(value);
        }
      };
    });
  }, [ackItemDetails]);

  // Generate columns with fixed order
  const columns = useMemo(() => {
    if (!processedData.length) return [];

    const baseColumns = [];

    // 1. ID column (first column from data)
    const keys = Object.keys(processedData[0]).filter(key => 
      !['bin_data', 'netsuite_response'].includes(key)
    );
    
    if (keys[0]) {
      baseColumns.push({
        ...createBaseColumn(keys[0]),
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined) {
            return '-';
          }
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          return String(value);
        }
      });
    }

    // 2. Username column
    baseColumns.push({
      id: 'username',
      accessorKey: 'username',
      header: 'Username',
      cell: info => {
        const value = info.getValue();
        
        // Handle different data types
        if (value === null || value === undefined || value === '') {
          return '-';
        }
        
        // Convert to string and trim
        const stringValue = String(value).trim();
        const result = stringValue || '-';
        return result;
      },
      size: 200,
      minSize: 180,
      enableSorting: true,
    });

    // 3. Bin Name column
    baseColumns.push({
      id: 'bin_name',
      accessorKey: 'bin_name',
      header: 'Bin Name',
      accessorFn: (row) => {
        const binData = row.bin_data || [];
        // bin_data is now an array after convertToTableFormat
        if (binData.length > 0 && binData[0]) {
          return binData[0].binName || binData[0].bin_name || '-';
        }
        return '-';
      },
      size: 200,
      minSize: 180,
      enableSorting: true,
    });

    // 4. NetSuite Response column (if exists)
    if (processedData[0].netsuite_response) {
      // Remove separate NetSuite button column. NetSuite response will be shown inside Bin Data dialog headers
    }

    // 5. Inventory ID column (if exists)
    if (processedData[0].inventory_id) {
      baseColumns.push({
        id: 'inventory_id',
        accessorKey: 'inventory_id',
        header: 'Inventory ID',
        cell: info => info.getValue(),
        size: 200,
        minSize: 180,
        enableSorting: true,
      });
    }

    // 6. Bin ID column (if exists)
    if (processedData[0].bin_id) {
      baseColumns.push({
        id: 'bin_id',
        accessorKey: 'bin_id',
        header: 'Bin ID',
        cell: info => info.getValue(),
        size: 200,
        minSize: 180,
        enableSorting: true,
      });
    }

    // 7. Bin Data column
    baseColumns.push({
      id: 'bin_data',
      ...createBaseColumn('bin_data'),
      header: 'Bin Data',
      cell: info => {
        const value = info.getValue();
        const hasData = Array.isArray(value) ? value.length > 0 : (value && typeof value === 'object' && Object.keys(value).length > 0);
        // Determine NetSuite success
        let success;
        try {
          const original = info.row?.original;
          success = original?.netsuite_response?.netsuite_acknowledgment?.success;
        } catch {}
          return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            {success === true && (
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4caf50' }} />
            )}
            {success === false && (
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#e53935' }} />
            )}
            <Typography variant="body2">{hasData ? 'View Details' : 'No Data'}</Typography>
          </Box>
        );
      },
    });

    // (Status indicator removed as it's rendered within Bin Data column)

    // 8. Created At column (if exists)
    if (processedData[0].created_at) {
      baseColumns.push({
        id: 'created_at',
        ...createBaseColumn('created_at'),
        header: 'Created At',
        cell: info => {
          const value = info.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date)) {
                return date.toLocaleString();
              }
            } catch (e) {
              return String(value);
            }
          }
          return value || '-';
        },
        size: 220,
        minSize: 200,
      });
    }

    // 9. Updated At column (if exists)
    if (processedData[0].updated_at) {
      baseColumns.push({
        id: 'updated_at',
        ...createBaseColumn('updated_at'),
        header: 'Updated At',
        cell: info => {
          const value = info.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date)) {
                return date.toLocaleString();
              }
            } catch (e) {
              return String(value);
            }
          }
          return value || '-';
        },
        size: 220,
        minSize: 200,
      });
    }

    // Add any remaining columns that weren't in the fixed order
    keys.slice(1).forEach(key => {
      if (!['username', 'netsuite_response', 'inventory_id', 'bin_id', 'created_at', 'updated_at'].includes(key)) {
        const columnExists = baseColumns.some(col => col.accessorKey === key);
        if (!columnExists) {
          baseColumns.push({
            ...createBaseColumn(key),
            cell: info => {
              const value = info.getValue();
              if (value === null || value === undefined) {
                return '-';
              }
              if (typeof value === 'object') {
                return JSON.stringify(value);
              }
              return String(value);
            }
          });
        }
      }
    });

    return baseColumns;
  }, [processedData]);

  const binTransferMainGrid = useDataGridProTable({
    data: filteredData,
    columns,
    loading: isLoading,
    error: error ? (error.message || 'Failed to load bin transfer records') : null,
    title: 'Bin Transfer Records',
    onDataRefresh: handleRefreshClick,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: (row) => {
      const value = row?.bin_data;
      const hasData = Array.isArray(value)
        ? value.length > 0
        : value && typeof value === 'object' && Object.keys(value).length > 0;
      if (!hasData) return;

      setBinDataOpen(true);
      setBinDataLoading(true);

      setTimeout(() => {
        setBinDataDetails(value);
        try {
          let ns = row?.netsuite_response || null;
          if (!ns && Array.isArray(value) && value.length > 0) {
            const first = value[0] || {};
            for (const [k, v] of Object.entries(first)) {
              if ((k === 'netsuite_response' || k.endsWith('_netsuite_response')) && v && typeof v === 'object') {
                ns = v;
                break;
              }
            }
          }
          setBinDataNetsuiteObj(ns);
        } catch {}
        setBinDataLoading(false);
      }, 100);
    },
    muiTableContainerProps: { sx: { flex: 1, minHeight: 0 } },
    initialSorting: [{ id: 'created_at', desc: true }],
    actionButtons: [
      <Box key="bin-transfer-filters" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Refresh Data">
          <IconButton
            color="primary"
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            sx={{ ml: 1 }}
          >
            {isRefreshing ? (
              <CircularProgress size={20} />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
        </Tooltip>
        <TextField
          select
          label="Location"
          size="small"
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          sx={{
            minWidth: 160,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        >
          {locationsList.map((loc) => (
            <MenuItem key={loc} value={loc} sx={{ fontSize: '0.8125rem' }}>
              {loc}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Date"
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getDateInputProps()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 150,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        />
        <TextField
          select
          label="View"
          size="small"
          value={successFilter}
          onChange={(e) => setSuccessFilter(e.target.value)}
          sx={{
            minWidth: 140,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
          <MenuItem value="success" sx={{ fontSize: '0.8125rem' }}>Success</MenuItem>
          <MenuItem value="errors" sx={{ fontSize: '0.8125rem' }}>Errors</MenuItem>
        </TextField>
      </Box>,
    ],
  });

  const binTransferBinDataGrid = useDataGridProTable({
    data: Array.isArray(binDataDetails) ? binDataDetails : binDataDetails ? [binDataDetails] : [],
    columns: binDataDetailColumns,
    loading: false,
    title: 'bin-transfer-bin-data',
    initialColumnVisibility: binDataColumnVisibility,
    onColumnVisibilityChange: setBinDataColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: (row) => {
      if (!row) return;
      try {
        const candidateEntries = Object.entries(row).filter(([k, v]) =>
          (k === 'itemData' || k.endsWith('_itemData')) && Array.isArray(v) && v.length > 0
        );
        if (candidateEntries.length > 0) {
          const [key, value] = candidateEntries[0];
          setNestedDialogOpen(true);
          setNestedDialogLoading(true);
          setTimeout(() => {
            setNestedDialogData(value);
            setNestedDialogTitle(`Bin Data - ${formatColumnHeader(key)}`);
            setNestedDialogLoading(false);
          }, 100);
        }
      } catch {}
    },
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: true,
    error: null,
  });

  const binTransferNestedGrid = useDataGridProTable({
    data: nestedDialogData || [],
    columns: nestedDialogColumns,
    loading: false,
    title: 'bin-transfer-nested',
    initialColumnVisibility: nestedColumnVisibility,
    onColumnVisibilityChange: setNestedColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    error: null,
  });

  const binTransferAckGrid = useDataGridProTable({
    data: ackItemDetails || [],
    columns: ackItemDetailsColumns,
    loading: false,
    title: 'bin-transfer-ack-items',
    initialColumnVisibility: ackItemDetailsColumnVisibility,
    onColumnVisibilityChange: setAckItemDetailsColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    error: null,
  });

  return (
    <DashboardLayout>
      <MuiXDataGridSurface hook={binTransferMainGrid} DataGridPro={DataGridPro} />

      {/* Bin Data Dialog */}
      <Dialog
        open={binDataOpen}
        onClose={() => setBinDataOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Bin Data Details</Typography>
            <IconButton
              onClick={() => setBinDataOpen(false)}
              sx={DIALOG_CLOSE_BUTTON_STYLE}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {binDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : binDataDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {(() => {
                // Prefer netsuite object captured from the parent row
                const nsObject = binDataNetsuiteObj;
                if (!nsObject) return null;
                const ns = nsObject.netsuite_response || nsObject;
                const ack = ns?.netsuite_acknowledgment || {};
                const safe = (v) => {
                  if (v === null || v === undefined) return '-';
                  if (typeof v === 'object') return JSON.stringify(v);
                  return String(v);
                };
                return (
                  <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, flexShrink: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      NetSuite Response Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, rowGap: 0.5, columnGap: 2 }}>
                      <Typography variant="body2" color="text.secondary">Message</Typography>
                      <Typography variant="body2">{safe(ns?.message)}</Typography>
                      <Typography variant="body2" color="text.secondary">Operation Time</Typography>
                      <Typography variant="body2">{safe(ns?.operation_time)}</Typography>
                      <Typography variant="body2" color="text.secondary">Success</Typography>
                      <Typography variant="body2">{ack?.success ? 'Yes' : 'No'}</Typography>
                      {ack?.inventoryAdjustmentId && (
                        <>
                          <Typography variant="body2" color="text.secondary">Inventory Adjustment ID</Typography>
                          <Typography variant="body2">{safe(ack.inventoryAdjustmentId)}</Typography>
                        </>
                      )}
                      {ack?.binTransferId && (
                        <>
                          <Typography variant="body2" color="text.secondary">Bin Transfer ID</Typography>
                          <Typography variant="body2">{safe(ack.binTransferId)}</Typography>
                        </>
                      )}
                    </Box>
                    {Array.isArray(ack?.itemDetails) && ack.itemDetails.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant={BUTTON_VARIANT}
                          size={BUTTON_SIZE}
                          sx={BUTTON_STYLE}
                          onClick={() => {
                            setAckItemDetails(ack.itemDetails);
                            setAckItemDetailsOpen(true);
                          }}
                        >
                          View Items ({ack.itemDetails.length})
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <MuiXDataGridSurface hook={binTransferBinDataGrid} DataGridPro={DataGridPro} />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* NetSuite Response Dialog */}
      

      {/* Nested Data Dialog */}
      <Dialog
        open={nestedDialogOpen}
        onClose={() => setNestedDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{nestedDialogTitle}</Typography>
            <IconButton
              onClick={() => setNestedDialogOpen(false)}
              sx={DIALOG_CLOSE_BUTTON_STYLE}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {nestedDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : nestedDialogData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={binTransferNestedGrid} DataGridPro={DataGridPro} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Acknowledgment Item Details Dialog */}
      <Dialog
        open={ackItemDetailsOpen}
        onClose={() => setAckItemDetailsOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">NetSuite Acknowledgment Item Details</Typography>
            <IconButton
              onClick={() => setAckItemDetailsOpen(false)}
              sx={DIALOG_CLOSE_BUTTON_STYLE}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {ackItemDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={binTransferAckGrid} DataGridPro={DataGridPro} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BinTransfer; 